import { useEffect, useState } from "react";
import { useRouter } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import { getConsent, subscribeConsent } from "@/lib/consent";
import { captureUtm } from "@/lib/tracking";

type TrackingConfig = {
  gtm_id?: string | null;
  ga4_measurement_id?: string | null;
  meta_pixel_id?: string | null;
  tiktok_pixel_id?: string | null;
  pinterest_tag_id?: string | null;
  snapchat_pixel_id?: string | null;
  microsoft_uet_id?: string | null;
};

/**
 * Loads marketing/analytics pixels based on user consent and admin-configured IDs.
 * Pixels DO NOT load until consent.marketing / .analytics is true.
 */
export function TrackingLoader() {
  const router = useRouter();
  const [config, setConfig] = useState<TrackingConfig | null>(null);
  const [consent, setConsentState] = useState(getConsent());

  useEffect(() => {
    captureUtm();
    supabase
      .rpc("get_tracking_pixels")
      .then(({ data }) => {
        const row = Array.isArray(data) ? data[0] : data;
        setConfig((row as TrackingConfig) ?? null);
      });
    return subscribeConsent(() => setConsentState(getConsent()));
  }, []);

  useEffect(() => {
    if (!config) return;
    if (consent.analytics) {
      if (config.gtm_id && !document.getElementById("gtm-script")) {
        const s = document.createElement("script");
        s.id = "gtm-script";
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtm.js?id=${config.gtm_id}`;
        document.head.appendChild(s);
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
      }
      if (config.ga4_measurement_id && !document.getElementById("ga4-script")) {
        const s = document.createElement("script");
        s.id = "ga4-script";
        s.async = true;
        s.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4_measurement_id}`;
        document.head.appendChild(s);
        (window as any).dataLayer = (window as any).dataLayer || [];
        // IMPORTANT: gtag.js only recognizes queued commands when pushed as an
        // `Arguments` object. Pushing a real Array (e.g. via rest params) causes
        // gtag.js to silently ignore `js` / `config` and never send `/g/collect`
        // hits. Keep this as a classic `function(){dataLayer.push(arguments)}`.
        (window as any).gtag = function () {
          // eslint-disable-next-line prefer-rest-params
          (window as any).dataLayer.push(arguments);
        };
        (window as any).gtag("js", new Date());
        // Honor ?_dbg=1 to surface this session in GA4 DebugView without an extension.
        const debugMode =
          typeof window !== "undefined" &&
          new URLSearchParams(window.location.search).get("_dbg") === "1";
        (window as any).gtag(
          "config",
          config.ga4_measurement_id,
          debugMode ? { debug_mode: true } : undefined,
        );
      }
    }
    if (consent.marketing) {
      if (config.meta_pixel_id && !(window as any).fbq) {
        /* eslint-disable */
        (function (f: any, b: any, e: any, v: any) {
          if (f.fbq) return;
          const n: any = (f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
          });
          if (!f._fbq) f._fbq = n;
          n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
          const t: any = b.createElement(e); t.async = true; t.src = v;
          const s: any = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
        (window as any).fbq("init", config.meta_pixel_id);
        (window as any).fbq("track", "PageView");
        /* eslint-enable */
      }
      // TikTok, Pinterest, Snap, MS UET loaders intentionally left as light placeholders to
      // keep this bundle small; wire each loader the same way once IDs are set.
    }
  }, [config, consent]);

  // SPA pageview tracking: GA4/GTM only auto-fire the initial pageview.
  // Re-emit page_view on every client-side route change.
  useEffect(() => {
    if (!config?.ga4_measurement_id && !config?.gtm_id) return;
    if (!consent.analytics) return;
    const unsub = router.subscribe("onResolved", ({ toLocation }) => {
      const page_path = toLocation.pathname + (toLocation.searchStr || "");
      const page_location =
        typeof window !== "undefined" ? window.location.origin + page_path : page_path;
      const page_title = typeof document !== "undefined" ? document.title : undefined;
      if (config.ga4_measurement_id && (window as any).gtag) {
        (window as any).gtag("event", "page_view", {
          page_path,
          page_location,
          page_title,
          send_to: config.ga4_measurement_id,
        });
      }
      if (config.gtm_id) {
        (window as any).dataLayer = (window as any).dataLayer || [];
        (window as any).dataLayer.push({
          event: "page_view",
          page_path,
          page_location,
          page_title,
        });
      }
    });
    return unsub;
  }, [router, config, consent.analytics]);

  return null;
}
